<template>
  <tr>
    <td class="text-center">
      <img v-if="item.brand && item.brand.logoLink && !item.brand.logoLink.includes('gravatar')" :src="item.brand.logoLink" class="brand-logo" />
      <span v-else-if="item.brand && item.brand.name"> {{ item.brand.name }} </span>
    </td>
    <td class="text-center">
      <div>
        <div v-if="item.back_order" class="d-inline">
          <span class="badge badge-primary">Pre-Order</span>
        </div>

        <div v-if="item.for_pets" class="d-inline">
          <span class="badge badge-primary">For Pets</span>
        </div>
      </div>
      <div class="nowrap">
        {{ item.product_name }}
      </div>
      <div class="nowrap text-small text-black-50">
        {{ item.cultivar_name ? `(${item.cultivar_name})` : "" }}
      </div>
      <div class="nowrap text-small text-black-50">
        {{ item.product_type ? `(${item.category_name} - ${item.product_type})` : item.category_name }}
      </div>
      <div v-if="item.lineNote" class="nowrap text-small text-black-50">
        {{ item.lineNote }}
      </div>
      <div v-if="item.hold" class="nowrap text-small text-black-50">
        (On Deck)
        <InfoTooltipBase
          :id="`${item.id}-ondeck-tooltip-1`"
          text="On Deck Items are only allowed in the cart if you have ordered all the available inventory as well"
          placement="left"
        />
      </div>

      <div
        v-if="
          item.unavailable ||
          (item.tracksInventory && (!item.sample || item.tracksSampleQuantity) && item.availableQuantity < quantityInCart(item, true))
        "
        class="text-danger"
      >
        <div v-if="!item.unavailable && item.availableQuantity > 0 && !item.sample">
          <h3>Inventory Stock Issue</h3>
          <BushelBtn
            v-if="!item.pack_to_order_quantity"
            class="ml-auto mr-auto"
            variant="success"
            @click="$emit('adjustToMaxQuantityInCart', item.id)"
          >
            {{ `${"Get Max Available"} (${item.availableQuantity})` }}
          </BushelBtn>
          <BushelBtn class="ml-2 mr-auto" variant="success" @click="isShowingModifyQuantityModal = true">
            <i :id="`editQuantity${item.id}`" class="fa fa-edit clickable" />
            Edit
          </BushelBtn>
          <BushelBtn class="ml-2 mr-auto" variant="danger" @click="$emit('removeFromCart', item.id)"> Delete </BushelBtn>
        </div>
        <div v-else>
          <h3>Out Of Stock</h3>
          <BushelBtn class="ml-2 mr-auto" variant="secondary" @click="$emit('removeFromCart', item.id)"> Delete </BushelBtn>
        </div>
      </div>
    </td>
    <td class="text-center">
      <span> {{ currency(item.unitPrice, "$") }} </span><br />
    </td>
    <td class="text-center">
      <div>
        {{ getQuantity() }}
        <div v-if="canModifyBasedOnHoldItems">
          <i :id="`editQuantity${item.id}`" class="fa fa-edit text-success clickable" @click="isShowingModifyQuantityModal = true" />
        </div>
        <div v-else>
          <InfoTooltipBase :id="`${item.id}-ondeck-tooltip-2`" :text="onDeckWarning" placement="left" />
        </div>
      </div>
      <div v-if="!item.sample" class="text-black-50 text-small">
        <p class="mb-0">
          {{ getPackageCaseOptions(item) }}
        </p>
        <p class="mb-0">
          {{ getUnitSize(item) }}
        </p>
        <p v-if="item.gram_per_preroll" class="mb-0">Weight: {{ item.gram_per_preroll }} Grams</p>
        <p v-if="item.packaged_unit_size" class="mb-0">Package Size: {{ item.packaged_unit_size }}</p>
      </div>
    </td>
    <td class="text-right">
      <div v-if="item.finalPrice != item.lineItemTotal && !item.sample" class="nowrap">
        <span> {{ currency(item.lineItemTotal, "$") }} </span>
      </div>
      <div v-if="item.tieredDiscount && !item.sample" class="nowrap">Customer Discount - {{ currency(item.tieredDiscount, "$") }}</div>
      <div v-if="item.bulkDiscount && !item.sample" class="nowrap">Bulk Discount - {{ currency(item.bulkDiscount, "$") }}</div>
      <div class="nowrap">
        <b>{{ currency(item.finalPrice, "$") }} </b>
      </div>
    </td>
    <td class="text-right" style="max-width: 40px">
      <div v-if="canModifyBasedOnHoldItems">
        <i class="fas fa-times text-danger close mr-3 clickable" @click="$emit('removeFromCart', item.id)" />
      </div>
      <div v-else class="mr-4">
        <InfoTooltipBase :id="`${item.id}-ondeck-tooltip-3`" :text="onDeckWarning" placement="left" />
      </div>
    </td>
    <ModifyQuantityLineItemCartModal
      :key="`ModifyQuantityLineItemCartModal-${item.id}`"
      :show="isShowingModifyQuantityModal"
      :item="item"
      :quantity-on-other-line-item="quantityInCart(item, true) - item.quantity"
      @hide="isShowingModifyQuantityModal = false"
      @modification-confirmed="handleUpdateQuantity"
    />
  </tr>
</template>
<script>
import { getPackageCaseOptions, getUnit, getUnitSize } from "@/services/storefront/storefront";
import ModifyQuantityLineItemCartModal from "./ModifyQuantityLineItemCartModal.vue";
import InfoTooltipBase from "@/components/common/InfoTooltipBase.vue";
import { currency } from "@/services/filters";

export default {
  components: { ModifyQuantityLineItemCartModal, InfoTooltipBase },
  props: {
    item: { type: Object, required: true },
    cartGroup: { type: Object, required: true },
  },
  emits: ["removeFromCart", "handleUpdateQuantity", "adjustToMaxQuantityInCart"],
  data() {
    return {
      isShowingModifyQuantityModal: false,
      onDeckWarning:
        "This item is required to be sold before other 'on deck' items in your cart. To modify this item, please remove the other 'on deck' items that came from the same product",
    };
  },
  computed: {
    canModifyBasedOnHoldItems() {
      const currentProductId = this.item.product_id;
      const itemsFromSameProductOnHoldWithLowerSortOrder = this.cartGroup.items.filter((i) => {
        if (i.product_id == currentProductId) {
          if (i.hold && i.sort_order > this.item.sort_order) {
            return true;
          }
        }
        return false;
      });
      if (itemsFromSameProductOnHoldWithLowerSortOrder.length) {
        return false;
      }

      return true;
    },
  },
  created() {},

  methods: {
    getPackageCaseOptions,
    getUnit,
    getUnitSize,
    currency,
    handleUpdateQuantity(data) {
      if (data && data.quantity !== null && data.quantity !== "" && data.quantity !== undefined) {
        if (data.quantity === 0) {
          this.$emit("removeFromCart", this.item.id);
        } else {
          this.$emit("handleUpdateQuantity", data);
        }
      }
    },

    quantityInCart(checkedItem, returnValue = false) {
      const cartItems = this.cartGroup.items;
      let returnData = 0;
      if (cartItems && cartItems.length) {
        const foundItems = cartItems.filter((item) => {
          if (item.batch_id == checkedItem.batch_id && item.product_id == checkedItem.product_id && !item.sample) {
            return true;
          }
          return false;
        });

        for (let i = 0; i < foundItems.length; i += 1) {
          returnData += foundItems[i].quantity;
        }
        if (returnValue) {
          return Number(returnData);
        }

        return `${returnData.toString()}`;
      }
      return 0;
    },
    getQuantity() {
      if (this.item.sample) {
        return "Sample";
      }
      if (this.item.pack_to_order_quantity) {
        return `${this.item.pack_to_order_quantity} - ${this.item.pack_to_order_type} ${"packs"} (${this.item.quantity} ${"Grams"})`;
      }
      const returnString = `${this.item.quantity} ${this.getUnit(this.item).name}`;
      return returnString;
    },
  },
};
</script>

<style scoped></style>
